import { LinkInterface as _LinkInterface } from './src/components/link/link';
import {
  MarketoFormInterface as _MarketoFormInterface,
  MarketoFormData as _MarketoFormData
} from './src/components/marketo-form/marketo-form';
import {
  FeatureFlagNetworkResponse as _FeatureFlagNetworkResponse
} from './src/utils/ab-tests/helpers';
import { TrackerEvent } from './src/utils/events/tracker-factory';
import { NetworkResponse as _NetworkResponse } from './src/utils/network/network-factory';
import { InputValidator as _InputValidator } from './src/utils/validators/form-validators';
import { MenuLink as _MenuLink } from './src/utils/getDrupalMenu';
import { Product as _Product } from './src/utils/googleTagManager';
import { EmailOnlyRegistrationStatus as _EmailOnlyRegistrationStatus } from './src/utils/registration';

interface DriftPayload {
  isOnline: boolean; // is the widget "online"
  sidebarOpen: boolean; // was the sidebar opened at widget launch
  teamAvailability: object;
  widgetVisible: boolean; // is the widget visible based on the drift display settings to the user (excludes any manual css overrides)
}

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
declare global {
  export interface Window {
    _wq: object[];
    ga: (
      str: string,
      obj: object | string,
      third?: object | string | null
    ) => void;
    dataLayer: object[] | null;
    drift: {
      on: (
        str: string,
        funct: (
          api: {
            startInteraction: (obj: object) => void;
          },
          payload: DriftPayload
        ) => void
      ) => void;
      page: () => void;
    };
    lucid: {
      getMetricLogger?: (
        str: string
      ) => {
        markLoaded: () => void;
        tag: (str: string, val?: string | boolean) => void;
      };
      kissMetrics?: {
        set: (val: { [x: string]: string | number }) => void;
        recordEvent: (val: string) => void;
      };
      beacon: {
        recordEvent: (evt: TrackerEvent) => void;
        recordPageChange: (val: string) => void;
        init: () => void;
      };
      cookieSyncClient: {
        syncAllCookies: () => void;
        syncAllMarketingCookies: () => void;
        init: (appDomain: string) => void;
      };
    } | null;
    Munchkin: {
      init: (value: string) => void;
      munchkinFunction: (value: string, { url }: { url: string }) => void;
    },
    optimizely: {
      push: any;
      get: any;
    };
    optimizelyData: any;
    optimizelyActionApplied: boolean | null;
    Wistia: {
      api: {
        all: () => Array<{
          email: (str: string) => void;
        }>;
      };
      localStorage: (key: string, value?: string) => string;
    };
  }
};

// theme
export { Theme, ThemeProps } from './src/theme/theme';
// utils
export {
  pollingFunction,
  getFeatureFlags,
  filterFeatureFlags,
  isTestActive,
  userIsRegisteredAndNotLoggedIn,
  getAllQueryParams
} from './src/utils/ab-tests/helpers';
export * from './src/utils/events/tracker-factory';
export { sendBeaconEvent } from './src/utils/events/send-beacon-event';
export { NetworkFactory } from './src/utils/network/network-factory';
export { InputDefaultValidator, InputPasswordValidator, InputEmailValidator } from './src/utils/validators/form-validators';
export { default as createMetatags } from './src/utils/createMetatags';
export { initialLandingPage } from './src/utils/cookies';
export { setupCookieSync } from './src/utils/cookieSync';
export { resetDrift, hideDrift, showDrift } from './src/utils/drift';
export {
  default as getEnv,
  isDev,
  isTest,
  isProd,
  isPreProd,
  isStaging
} from './src/utils/env';
export { default as formatTranslationPath } from './src/utils/formatTranslationPath';
export { default as getDrupalMenu } from './src/utils/getDrupalMenu';
export { default as getLinkPath } from './src/utils/getLinkPath';
export { default as gtmConfig } from './src/utils/googleTagManager';
export { default as i18nFunct } from './src/utils/i18n';
export { default as getInternalLink } from './src/utils/internalLink';
export { default as isBrowser } from './src/utils/isBrowser';
export { default as isBrowserIE } from './src/utils/isBrowserIE';
export { default as localizePhoneNumbers } from './src/utils/localizePhoneNumbers';
export { default as lucidColors } from './src/utils/lucidColors';
export { Marketo } from './src/utils/marketo';
export {
  default as marketoFormIds,
  allMarketoForms,
  checkForMarketoFields,
  retrieveMarketoIds
} from './src/utils/marketoForms';
export { default as getProcessedValue } from './src/utils/processedValue';
export { default as productDomains, productDomainEnv } from './src/utils/productDomains';
export {
  registerUserEmailOnly,
  registerFullFields
} from './src/utils/registration';
export { default as shortLangPrefix } from './src/utils/shortLangPrefix';
export { default as withCookieSyncParams } from './src/utils/withCookieSyncParams';
// components
export { default as LucidContainer } from './src/components/lucid-container/lucid-container';
export { default as BounceDetection } from './src/components/bounce-detection/bounce-detection';
export { default as FontLoader } from './src/components/font-loader/font-loader';
export { default as Link } from './src/components/link/link';
export { default as linkParser } from './src/components/link/link-parser';
export { default as MarketoForm } from './src/components/marketo-form/marketo-form';
export { default as Pingdom } from './src/components/pingdom/pingdom';
export { default as SEO } from './src/components/seo/seo';
export { default as WistiaEmbed } from './src/components/wistia-embed/wistia-embed';
// hooks
export { useCookie } from './src/hooks/use-cookie';
export { default as usePathPrefix } from './src/hooks/use-path-prefix';
export { default as useSiteMetadata } from './src/hooks/use-site-metadata';
// types
export type FeatureFlagNetworkResponse = _FeatureFlagNetworkResponse;
export type InputValidator = _InputValidator;
export type EmailOnlyRegistrationStatus = _EmailOnlyRegistrationStatus;
export { VARIATION_TYPES } from './src/utils/ab-tests/helpers';
export type LinkInterface = _LinkInterface;
export {
  MarketingPageElementLocation,
  MarketingPageElementType,
  ModalName,
  InteractedWithModalAction
} from './src/types/beaconEventTypes';
export type MarketoFormData = _MarketoFormData;
export type MarketoFormInterface = _MarketoFormInterface;
export type MenuLink = _MenuLink;
export type NetworkResponse = _NetworkResponse;
export type Product = _Product;
export * from './src/types/drupal-base-types';
export * from './src/types/drupal-types';
export * from './src/types/i18n-types';
export * from './src/types/marketo-types';
export * from './src/types/plugin-types';
export * from './src/types/types';
